import { zodResolver } from '@hookform/resolvers/zod';
import * as Sentry from '@sentry/react';
import { EyeIcon } from 'lucide-react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import useSWR from 'swr';
import { z } from 'zod';

import { Loading } from '@/components/Loading';
import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { apiFetcher } from '@/data/fetcher';
import { ParticipantForm } from '@/forms/ParticipantForm';
import { Collection } from '@/interfaces/Collection';
import { DocumentData } from '@/interfaces/DocumentData';
import { Expense } from '@/interfaces/Expense';
import { Invoice } from '@/interfaces/Invoice';
import { Settings } from '@/interfaces/Settings';
import API from '@/lib/api';
import { formatDate } from '@/lib/formats/formatDate';
import { components } from '@invo.space/shared';
import { Currency } from '@/types/Currency';

import { DocumentItemsForm } from '../DocumentItemsForm';
import { InvoiceSettingsForm } from '../InvoiceSettingsForm';
import { DocumentPreview } from './components/DocumentPreview'
import { Totals } from './components/Totals';
import { ExpenseFormSchema, InvoiceFormSchema, OrderFormSchema } from './schema';

const DocumentFormSchema = z.discriminatedUnion('type', [
  ExpenseFormSchema,
  InvoiceFormSchema,
  OrderFormSchema,
])

export type DocumentFormValues = z.infer<typeof DocumentFormSchema>

interface DocumentFormProps {
  type: 'expense' | 'invoice' | 'order'
  documentData?: Partial<
    DocumentData &
      Pick<Invoice, 'taxableAt'> &
      Pick<
        Expense,
        | 'expenseType'
        | 'receivedAt'
        | 'vatDeductible'
        | 'vatDeductionProportionPercent'
      >
  >
  settings: Settings
  onSave?: (document: components['schemas']['Document']) => void
  showBeneficiary?: boolean
  // currencies: Currency[]
}

/** Sekce editoru: nadpis a obsah, oddělení dělá jen odsazení (bez rámečků). */
const EditorSection = ({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) => (
  <section className="grid content-start gap-4">
    <div className="grid gap-0.5">
      <h2 className="text-base font-medium tracking-[-0.01em]">{title}</h2>
      {description && <p className="text-[13px] text-muted-foreground">{description}</p>}
    </div>
    {children}
  </section>
)

function entityToFormValues(entity: components['schemas']['Participant']) {
  return {
    ...entity,
    businessId: entity.ico,
    taxId: entity.dic,
  }
}

export const DocumentForm = ({
  type,
  documentData,
  settings,
  onSave,
  showBeneficiary = false,
}: DocumentFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  const { supplier, customer, beneficiary } = documentData || {}

  const { data: currencies } = useSWR<Collection<Currency>>(
    `/api/v2/currencies`,
    apiFetcher,
  )

  type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']
  const { data: bankAccountsData } = useSWR<{ data?: InvoicingBankAccount[] }>(
    type !== 'expense' ? '/api/v2/bank-accounts' : null,
    apiFetcher,
  )

  const isVatPayer = settings.vatPayer === 'vatPayer'

  const items = useMemo(
    () =>
      documentData?.items?.map((item) => {
        return {
          ...item,
          unitPrice: item?.unitPriceTaxFree?.amount
            ? Number(item?.unitPriceTaxFree?.amount) / 100
            : null,
        }
      }) || [
        {
          amount: 1,
          unit: '',
          description: '',
          taxRate: isVatPayer ? 21 : undefined,
          unitPrice: null,
          skuId: null,
        },
      ],
    [documentData],
  )

  const { t } = useTranslation()
  const methods = useForm<DocumentFormValues>({
    resolver: zodResolver(DocumentFormSchema),
    defaultValues: {
      type: type,
      supplier: supplier ? entityToFormValues(supplier) : undefined,
      customer: customer ? entityToFormValues(customer) : {
        name: '',
        address: {
          country: 'CZ',
        }
      },
      beneficiary: beneficiary ? entityToFormValues(beneficiary) : undefined,
      settings: {
        issueDate: formatDate(
          documentData?.issuedAt
            ? new Date(documentData?.issuedAt)
            : new Date(),
        ),
        taxDate:
          type !== 'order' && settings?.vatPayer === 'vatPayer'
            ? formatDate(
                documentData?.taxableAt
                  ? new Date(documentData?.taxableAt)
                  : new Date(),
              )
            : undefined,
        receivedDate:
          type === 'expense'
            ? formatDate(
                documentData?.receivedAt
                  ? new Date(documentData?.receivedAt)
                  : new Date(),
              )
            : undefined,
        dueDate: formatDate(
          documentData?.deadlineAt
            ? new Date(documentData?.deadlineAt)
            : new Date(),
        ),
        currency: documentData?.currency ?? settings.primaryCurrency,
        variableSymbol: documentData?.vs,
        paymentMethod: documentData?.paymentMethod ?? 'bankTransfer',
        bankAccountId: undefined,
        expenseType:
          type === 'expense'
            ? documentData?.expenseType || 'invoice'
            : undefined,
        vatDeductible:
          type === 'expense' ? documentData?.vatDeductible || true : undefined,
        vatDeductionProportionPercent:
          type === 'expense'
            ? !documentData || documentData.vatDeductible
              ? Number(documentData?.vatDeductionProportionPercent || 100)
              : undefined
            : undefined,
      },
      items: items,
      description: documentData?.description,
    },
  })
  const { handleSubmit } = methods

  useEffect(() => {
    // console.log('Applying invoice data', supplier, customer)

    if (customer) {
      methods.setValue('customer', entityToFormValues(customer))
    }

    if (beneficiary) {
      methods.setValue('beneficiary', entityToFormValues(beneficiary))
    }

    if (items) {
      methods.setValue('items', items)
    }
  }, [documentData, methods, supplier, customer, beneficiary, items])

  const currentBankAccountId = methods.watch('settings.bankAccountId')

  useEffect(() => {
    if (type === 'expense' || !bankAccountsData?.data?.length) return

    if (currentBankAccountId) return

    const sba = documentData?.supplierBankAccount
    if (sba) {
      const match = bankAccountsData.data.find(
        (a) =>
          (sba.accountNumber && a.accountNumber === sba.accountNumber && a.bankCode === sba.bankCode) ||
          (sba.iban && a.iban === sba.iban),
      )
      if (match) {
        methods.setValue('settings.bankAccountId', match.id!)
        return
      }
    }

    const customerDefaultId = customer?.defaultBankAccountId
    if (customerDefaultId) {
      const exists = bankAccountsData.data.some((a) => a.id === customerDefaultId)
      if (exists) {
        methods.setValue('settings.bankAccountId', customerDefaultId)
        return
      }
    }

    const defaultAccount = bankAccountsData.data.find((a) => a.isDefault)
    if (defaultAccount) {
      methods.setValue('settings.bankAccountId', defaultAccount.id!)
    }
  }, [bankAccountsData, customer, documentData, type, methods, currentBankAccountId])

  const isEdit = !!documentData?.uuid
  const buttonLabel = isEdit
    ? t(`${type}.edit.submit.label`)
    : t(`${type}.new.submit.label`)

  const onValid = (values: DocumentFormValues) => {
    setIsSubmitting(true)
    // console.log('Form submitting...', values)

    const [endpoint, appEndpoint] = {
      invoice: ['invoices', 'invoice'],
      order: ['orders', 'order'],
      expense: ['expenses', 'expense'],
    }[type]

    if (isEdit) {
      API.patch(`/api/v2/${endpoint}/${documentData?.uuid}`, {
        // customer: values.customer,
        customer:
          type !== 'expense'
            ? {
                ...values.customer,
                id: undefined,
                ico: values.customer.businessId,
                dic: values.customer.taxId,
                businessId: undefined,
                taxId: undefined,
              }
            : undefined,
        supplier:
          type === 'expense'
            ? {
                ...values.supplier!,
                id: undefined,
                ico: values.supplier!.businessId,
                dic: values.supplier!.taxId,
                businessId: undefined,
                taxId: undefined,
              }
            : undefined,
        issuedAt: moment(
          values.settings.issueDate!,
          'DD.MM.YYYY',
        ).toISOString(),
        taxableAt: values.settings.taxDate
          ? moment(values.settings.taxDate, 'DD.MM.YYYY').toISOString()
          : undefined,
        deadlineAt: moment(
          values.settings.dueDate!,
          'DD.MM.YYYY',
        ).toISOString(),
        receivedAt:
          type === 'expense'
            ? moment(values.settings.receivedDate!, 'DD.MM.YYYY').toISOString()
            : undefined,
        expenseType:
          type === 'expense' ? values.settings.expenseType : undefined,
        vatDeductible:
          type === 'expense' ? values.settings.vatDeductible : undefined,
        vatDeductionProportionPercent:
          type === 'expense'
            ? values.settings.vatDeductionProportionPercent
            : undefined,
        vs: values.settings.variableSymbol,
        paymentMethod: values.settings.paymentMethod,
        bankAccountId: type !== 'expense' ? values.settings.bankAccountId : undefined,
        items: values.items.map((item) => {
          return {
            amount: item.amount,
            unit: item.unit,
            unitPrice: {
              amount: (item.unitPrice! * 100).toFixed(0),
              currency: values.settings.currency,
            },
            description: item.description,
            taxRate: isVatPayer ? item.taxRate : undefined,
            skuId: item.skuId ?? undefined,
            lineExtras: item.lineExtras ?? null,
          }
        }),
        currency: values.settings.currency,
        description: values.description,
      } as components['schemas']['PatchDocumentRequest'])
        .then(() => {
          toast.success(t(`${type}.edit.success.title`), {
            duration: 4000,
            closeButton: true,
          })
        })
        .catch((error) => {
          if (!error.response || !error.response.data) {
            Sentry.logger.error('No response data', {
              error: error,
              formValues: values,
            })

            toast.error(t(`error.document.save`), {
              duration: 4000,
              closeButton: true,
            })
            return
          }

          const { code, detail, status, title, type } = error.response.data

          console.log(detail, status, title, type)

          if (code === 40402) {
            toast.error(t('error.40402'), {
              duration: 4000,
              closeButton: true,
            })
          } else if (code === 40014) {
            const { requestedYear, existingYear } = error.response.data

            toast.error(
              t('error.40014', { requestedYear, existingYear }),
              {
                duration: 6000,
                closeButton: true,
              },
            )
          } else if (code === 50302) {
            toast.error(t('ares.temporarilyUnavailable'), {
              duration: 4000,
              closeButton: true,
            })
          } else {
            toast.error(t(`error.document.save`), {
              duration: 4000,
              closeButton: true,
            })
          }
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    } else {
      API.post<components['schemas']['DocumentIdentificationResponse']>(
        `/api/v2/${endpoint}`,
        {
          customer:
            type !== 'expense'
              ? values.customer.id
                ? values.customer.id
                : {
                    ...values.customer,
                    ico: values.customer.businessId,
                    dic: values.customer.taxId,
                    businessId: undefined,
                    taxId: undefined,
                  }
              : undefined,
          supplier:
            type === 'expense'
              ? values.supplier!.id
                ? values.supplier!.id
                : {
                    ...values.supplier!,
                    ico: values.supplier!.businessId,
                    dic: values.supplier!.taxId,
                    businessId: undefined,
                    taxId: undefined,
                  }
              : undefined,
          issuedAt: moment(
            values.settings.issueDate!,
            'DD.MM.YYYY',
          ).toISOString(),
          taxableAt: values.settings.taxDate
            ? moment(values.settings.taxDate, 'DD.MM.YYYY').toISOString()
            : undefined,
          receivedAt:
            type === 'expense'
              ? moment(
                  values.settings.receivedDate!,
                  'DD.MM.YYYY',
                ).toISOString()
              : undefined,
          expenseType:
            type === 'expense' ? values.settings.expenseType : undefined,
          vatDeductible:
            type === 'expense' ? values.settings.vatDeductible : undefined,
          deadlineAt: moment(
            values.settings.dueDate!,
            'DD.MM.YYYY',
          ).toISOString(),
          paidAt: documentData?.paidAt ? moment(documentData?.paidAt).toISOString() : undefined,
          vs: values.settings.variableSymbol,
          vatDeductionProportionPercent:
            type === 'expense'
              ? values.settings.vatDeductionProportionPercent
              : undefined,
          paymentMethod: values.settings.paymentMethod,
          bankAccountId: type !== 'expense' ? values.settings.bankAccountId : undefined,
          items: values.items.map((item) => {
            return {
              amount: item.amount,
              unit: item.unit,
              unitPrice: {
                amount: (item.unitPrice! * 100).toFixed(0),
                currency: values.settings.currency,
              },
              description: item.description,
              taxRate: item.taxRate,
              skuId: item.skuId ?? undefined,
              lineExtras: item.lineExtras ?? null,
            }
          }),
          currency: values.settings.currency,
          description: values.description,
        } as components['schemas']['CreateDocumentRequest'],
      )
        .then((response) => {
          toast.success(t(`${type}.new.success.title`), {
            duration: 4000,
            closeButton: true,
          })

          if (onSave) {
            onSave(response.data as components['schemas']['Document'])
          } else {
            navigate(`/${appEndpoint}/${response.data.id}`)
          }
        })
        .catch((error) => {
          if (!error.response || !error.response.data) {
            Sentry.logger.error('No response data', {
              error: error,
              formValues: values,
            })

            toast.error(t(`error.document.save`), {
              duration: 4000,
              closeButton: true,
            })
            return
          }

          const { code, detail, status, title, type } = error.response.data

          console.log(detail, status, title, type)

          if (code === 40402) {
            toast.error(t('error.40402'), {
              duration: 4000,
              closeButton: true,
            })
          } else if (code === 40014) {
            const { requestedYear, existingYear } = error.response.data

            toast.error(
              t('error.40014', { requestedYear, existingYear }),
              {
                duration: 6000,
                closeButton: true,
              },
            )
          } else if (code === 50302) {
            toast.error(t('ares.temporarilyUnavailable'), {
              duration: 4000,
              closeButton: true,
            })
          } else {
            toast.error(t(`error.document.save`), {
              duration: 4000,
              closeButton: true,
            })
          }
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    }
  }

  const onInvalid = (errors: any) => {
    console.log('Form errors', errors, methods.getValues())
  }

  const preview = (
    <DocumentPreview
      type={type}
      settings={settings}
      bankAccounts={bankAccountsData?.data}
      documentNumber={documentData?.symbol}
    />
  )

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onValid, onInvalid)}>
        <div className="mx-auto grid w-full max-w-[1320px] items-start gap-10 xl:grid-cols-[minmax(0,720px)_minmax(0,1fr)] 2xl:gap-14">
          <div className="mx-auto grid w-full min-w-0 max-w-[720px] content-start gap-10 xl:mx-0">
            <EditorSection
              title={t(
                type === 'expense'
                  ? 'participant.supplier.title'
                  : 'participant.customer.title',
              )}
            >
              <ParticipantForm
                prefix={type === 'expense' ? 'supplier' : 'customer'}
              />
            </EditorSection>

            {type !== 'expense' && showBeneficiary && (
              <EditorSection title={t('participant.beneficiary.title')}>
                <ParticipantForm prefix="beneficiary" />
              </EditorSection>
            )}

            <EditorSection title={t('documentForm.items.title')}>
              <DocumentItemsForm
                currencies={currencies?.data}
                isVatPayer={isVatPayer}
              />
            </EditorSection>

            <EditorSection title="Platba a termíny">
              <InvoiceSettingsForm type={type} settings={settings} />
            </EditorSection>

            <EditorSection
              title="Poznámka na dokladu"
              description="Zobrazí se na dokladu nad položkami."
            >
              <FormField
                control={methods.control}
                name={`description`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder={t('documentForm.description.placeholder')}
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </EditorSection>

            <div className="sticky bottom-0 z-10 -mb-6 flex items-center justify-between gap-3 border-t bg-background py-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <Totals
                control={methods.control}
                currencies={currencies?.data}
                isVatPayer={isVatPayer}
              />

              <div className="flex shrink-0 items-center gap-2">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button type="button" variant="outline" size="lg" className="xl:hidden max-sm:px-3" aria-label="Náhled">
                      <EyeIcon />
                      <span className="hidden sm:inline">Náhled</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent className="overflow-y-auto sm:max-w-[560px]">
                    <SheetHeader className="px-6 pt-6">
                      <SheetTitle>Náhled dokladu</SheetTitle>
                      <SheetDescription className="sr-only">Živý náhled dokladu podle vyplněných údajů</SheetDescription>
                    </SheetHeader>
                    <div className="p-6 pt-4">{preview}</div>
                  </SheetContent>
                </Sheet>

                <Button type="submit" size="lg" disabled={isSubmitting}>
                  {isSubmitting && <Loading className="w-4 h-4 mr-2" />}
                  {buttonLabel}
                </Button>
              </div>
            </div>
          </div>

          <aside className="hidden xl:sticky xl:top-[calc(var(--header-height)+1.5rem)] xl:block">
            <div className="mx-auto w-full max-w-[min(100%,calc((100svh-var(--header-height)-5.5rem)/1.414))]">
              <p className="mb-2 text-xs text-muted-foreground">Náhled dokladu</p>
              {preview}
            </div>
          </aside>
        </div>
      </form>
    </FormProvider>
  )
}
